<template lang="pug">
	.audio-tracker-preset-selector.flex-row
		.ui-button.round.scale.flex-row.align-center.center(v-if="presetData", @click="() => showSavePresetDialog()", :title="$tCaption('Save preset')")
			i.fas.fa-save
		DxDropDownButton(ref="dropdown", :items="presets", keyExpr="name", :text="presetTitle", displayExpr="name", :dropDownOptions="dropDownOptions", :focusStateEnabled="false", @itemClick="v => selectPreset(v.itemData)")
			template(v-slot:item-data="{ data }")
				.audio-tracker-preset-selector
					.flex-row.justify.align-center
						.flex-row.flex-1
							.preset-name {{data.name}}
						.right
							.ui-button.round.scale.flex-row.align-center.center(v-if="data.allowDelete", @click.stop="(e) => removePreset(data)", :title="$tCaption('Remove preset')")
								i.fas.fa-times
		Popup(v-if="newPresetPopupVisible", :popupVisible="newPresetPopupVisible", :width="320", :height="230", classes="tracker-dark", :title="$tCaption('Save preset')")
			.flex-col.field.space
				.font-small.upper.grey {{$tCaption('Preset name')}}
				DxTextBox(:value.sync="newPresetName", width="100%", :input-attr="{ autocomplete: 'off' }", valueChangeEvent="input")
			template(slot="actions-right")
				DxButton(:text="isExistingPresetName ? $tCaption('Update') : $tCaption('Save new')", :type="isExistingPresetName ? 'default' : 'success'", @click="savePreset", :disabled="isLoading || !newPresetAllowed")
				DxButton(:text="$tCaption('Cancel', '@actions')", @click="newPresetPopupVisible = false")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import presets from './presets';
	import DxDropDownButton from 'devextreme-vue/drop-down-button';
	import DxButton from 'devextreme-vue/button';
	import DxTextBox from 'devextreme-vue/text-box';
	import Popup from 'app-core/Popup.vue';

	export default {
		translationCategory: 'audioTracker.AudioTrackerPresetSelector',
		name: 'audio-tracker-preset-selector',
		components: { DxDropDownButton, DxButton, Popup, DxTextBox },
		props: {
			type: VueTypes.string.def(null),
			presetData: VueTypes.any.def(null),
			storedPreset: VueTypes.any.def(null)
		},
		data() {
			return {
				selectedPreset: null,
				presets: [],
				newPresetPopupVisible: false,
				newPresetName: null,
				isLoading: false,
				dropDownOptions: { 
					minWidth: '220px',
					maxHeight: '400px'
				}
			};
		},
		watch: {
			type(v) {
				this.loadPresets();
			},
		},
		mounted() {
			if (__DEBUG__)
				window.presetSelector = this;
			this.loadPresets();
		},
		computed: {
			newPresetAllowed() {
				return this.newPresetName && !this.presets.some(f => f.name === this.newPresetName && !f.allowDelete);
			},
			isExistingPresetName() {
				return this.newPresetName && this.presets.some(f => f.name === this.newPresetName && f.allowDelete);
			},
			isSameAsStoredPreset() {
				if (!this.storedPreset) return true;

				return JSON.stringify(this.presetData) == JSON.stringify(this.storedPreset.data);
			},
			presetTitle() {
				return this.storedPreset?.name ? ((this.storedPreset?.name?.substr(0, 30) + (this.storedPreset?.name?.length > 30 ? '..' : '')) + (this.isSameAsStoredPreset ? '' : '*')) : this.$tCaption('Presets');
			}
		},
		methods: {
			loadPresets() {
				return presets.getPresets(this.type).then(r => {
					if (__DEBUG__)
						console.debug('Presets loaded', { r });
					if (r && Array.isArray(r))
						this.presets = r.map(f => ({ ...f, template: 'item-data' }));

					if (this.storedPreset) {
						this.selectedPreset = this.presets.find(f => f.id === this.storedPreset.id || f.name === this.storedPreset.name);
					}
					return r;
				});
			},
			selectPreset(preset) {
				if (__DEBUG__)
					console.debug('Select preset', { preset });
				this.selectedPreset = preset;
				this.$emit('select-preset', _.cloneDeep(preset?.data || null), preset);
			},
			removePreset(preset) {
				if (__DEBUG__)
					console.debug('Remove preset', { preset });
				this.isLoading = true;

				presets.removePreset(preset).then(() => {
					if (__DEBUG__)
						console.debug('Preset removed');
					this.presets = this.presets.filter(f => f.id !== preset.id);
				}).finally(() => {
					this.isLoading = false;
				});
			},
			savePreset() {
				var preset = {
					id: this.newPresetName,
					name: this.newPresetName,
					type: this.type,
					data: _.cloneDeep(this.presetData),
					template: 'item-data',
					allowDelete: true
				};
				if (__DEBUG__)
					console.debug('Save preset', { preset });
				this.isLoading = true;
				presets.savePreset(preset).then(() => {
					this.loadPresets();
					this.newPresetPopupVisible = false;
				}).finally(() => {
					this.isLoading = false;
				});
			},
			showSavePresetDialog() {
				this.newPresetPopupVisible = false;
				this.newPresetName = this.selectedPreset?.name || `${this.$tCaption('New preset')} ${this.presets.length + 1}`;
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.newPresetPopupVisible = true;
				})
			}
		}
	};
</script>
<style lang='scss'>
	.audio-tracker-preset-selector {
		@import "main/themes/generated/variables.base.scss";

		.preset-name {
			padding-left: 10px;
		}
	}
</style>
